import { useState } from 'react';
import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';
import useRecurrenceParameters from './useRecurrenceParameters';

export const defaultValue = {
  dates: [],
  shouldAutoFill: true,
  businessDaysOnly: true,
};

function getKey(sampleId, assayId, panelId) {
  if (!sampleId || !assayId) return;
  return JSON.stringify(
    panelId ? [sampleId, assayId, panelId] : [sampleId, assayId]
  );
}

export default function useRecurrence() {
  const { isEnabled } = useRecurrenceParameters();
  const [recurrence, addRecurrence] = useState({});

  function getRecurrence({ sampleId, assayId, panelId }) {
    if (!isEnabled) return defaultValue;
    return recurrence[getKey(sampleId, assayId, panelId)] || defaultValue;
  }

  function setRecurrence({ sampleId, assayId, panelId, ...value }) {
    if (!isEnabled) return;
    const key = getKey(sampleId, assayId, panelId);
    const current = getRecurrence({ sampleId, assayId, panelId });
    addRecurrence((prev) => ({
      ...prev,
      [key]: {
        ...current,
        ...value,
      },
    }));
  }

  function removeRecurrence({ sampleId, assayId, panelId }) {
    if (!isEnabled) return;

    let entRegex;
    if (assayId && panelId) {
      // Scoped to a specific assay within a specific panel.
      entRegex = `^\\[[1-9]+,${assayId},${panelId}\\]$`;
    } else if (sampleId) {
      entRegex = `/^\[${sampleId},[1-9]+(,[1-9]+)?\]$/`;
    } else if (assayId) {
      entRegex = `/^\[[1-9]+,${assayId},[1-9]+\]$/`;
    } else if (panelId) {
      entRegex = `/^\[[1-9]+,[1-9]+,${panelId}\]$/`;
    }
    const regex = new RegExp(entRegex);
    const newVal = omitBy(recurrence, (_, key) => !regex.test(key));
    addRecurrence(newVal);
  }

  function checkEditing({ id, sampleId, isPanel }) {
    return (
      Object.keys(recurrence)
        .map(JSON.parse)
        // Panel-based recurrences...
        .filter((ids) => (ids.length === isPanel ? 3 : 2))
        // with ID matching `panelId`.
        .some(
          (ids) =>
            ids.at(0) === parseInt(sampleId) && ids.at(-1) === parseInt(id)
        )
    );
  }

  function createPayload(testPayload) {
    if (!isEnabled) return testPayload;
    return testPayload
      .map((payload) => {
        const {
          estimated_start_date: _,
          estimated_complete_date: __,
          ...test
        } = payload;
        const { sample_id: sampleId, assay_id: assayId, panel_id: panelId } = test;
        const { dates } = getRecurrence({
          assayId,
          sampleId,
          ...(panelId && { panelId }),
        });

        return isEmpty(dates)
          ? payload
          : dates.map(({ start, end }) => ({
              ...test,
              ...(start && { estimated_start_date: start }),
              ...(end && { estimated_complete_date: end }),
            }));
      })
      .flat(2);
  }

  return {
    createPayload,
    checkEditing,
    getRecurrence,
    setRecurrence,
    removeRecurrence,
    recurrence: isEnabled ? recurrence : {},
  };
}
